/* eslint-disable react-hooks/set-state-in-effect */
'use client'

import { useState, useEffect, useCallback } from 'react'
import { useInView } from 'react-intersection-observer'
import { Loader2, Play, ChevronLeft, ChevronRight } from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'

import InstagramIcon from '@/components/InstagramIcon'
import LinkedInIcon from '@/components/LinkedInIcon'
import TikTokIcon from '@/components/TikTokIcon'
import FacebookIcon from '@/components/FacebookIcon'
import SocialPostDialog from '@/components/SocialPostDialog'
import { api } from '@/utils/trpc'

type ScheduleTab = 'SCHEDULED' | 'PUBLISHED' | 'UNSCHEDULED'

type SocialPostGridProps = {
  scheduleStatus: ScheduleTab
}

type Asset = {
  type: string
  url: string | null
  thumbnailUrl: string | null
  title: string | null
}

type Item = {
  id: string
  platform: string
  caption: string
  hashtags: string[]
  publishAt: Date | string | null
  assets: Asset[]
}

const PLATFORM_LABEL: Record<string, string> = {
  INSTAGRAM: 'Instagram',
  LINKEDIN: 'LinkedIn',
  TIKTOK: 'TikTok',
  FACEBOOK: 'Facebook',
}

function PlatformIconFor({
  platform,
  className,
}: {
  platform: string
  className?: string
}) {
  const cls = className ?? 'size-4'
  if (platform === 'LINKEDIN') return <LinkedInIcon className={cls} />
  if (platform === 'TIKTOK') return <TikTokIcon className={cls} />
  if (platform === 'FACEBOOK') return <FacebookIcon className={cls} />
  return <InstagramIcon className={cls} />
}

function formatPublishAt(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale === 'de' ? 'de-DE' : 'en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
    hour12: locale !== 'de',
  }).format(date)
}

function groupConsecutiveByPlatform(items: Item[]): {
  platform: string
  items: Item[]
}[] {
  const groups: { platform: string; items: Item[] }[] = []
  for (const item of items) {
    const last = groups[groups.length - 1]
    if (last && last.platform === item.platform) {
      last.items.push(item)
    } else {
      groups.push({ platform: item.platform, items: [item] })
    }
  }
  return groups
}

const PAGE_SIZE = 25

export default function SocialPostGrid({
  scheduleStatus,
}: SocialPostGridProps) {
  const t = useTranslations('SocialPostGrid')
  const locale = useLocale()
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [items, setItems] = useState<Item[]>([])
  const [hasMore, setHasMore] = useState(true)
  const [isFetchingMore, setIsFetchingMore] = useState(false)

  const firstPageQuery = api.instagramPosts.list.useQuery(
    {
      scheduleStatus,
      statuses: ['SUCCESS'],
      page: 1,
      pageSize: PAGE_SIZE,
    },
    {
      staleTime: 1000 * 60 * 5,
    },
  )

  useEffect(() => {
    if (firstPageQuery.data) {
      const d = firstPageQuery.data as { items: unknown[]; hasMore: boolean }
      setItems(d.items as unknown as Item[])
      setHasMore(d.hasMore)
    }
  }, [firstPageQuery.data])

  const trpcUtils = api.useContext()

  const fetchMore = useCallback(async () => {
    if (isFetchingMore || !hasMore) return
    setIsFetchingMore(true)
    const nextPage = page + 1
    try {
      const result = await trpcUtils.instagramPosts.list.fetch({
        scheduleStatus,
        statuses: ['SUCCESS'],
        page: nextPage,
        pageSize: PAGE_SIZE,
      })
      const newItems = result.items as unknown as Item[]
      if (newItems.length > 0) {
        setItems((prev) => [...prev, ...newItems])
        setPage(nextPage)
        setHasMore(result.hasMore)
      } else {
        setHasMore(false)
      }
    } catch {
      // ignore
    } finally {
      setIsFetchingMore(false)
    }
  }, [page, hasMore, isFetchingMore, scheduleStatus, trpcUtils])

  const { ref: inViewRef, inView } = useInView({
    threshold: 0.5,
    rootMargin: '200px',
  })

  useEffect(() => {
    if (inView && hasMore && !isFetchingMore) {
      fetchMore()
    }
  }, [inView, hasMore, isFetchingMore, fetchMore])

  useEffect(() => {
    setItems([])
    setPage(1)
    setHasMore(true)
    setIsFetchingMore(false)
    if (firstPageQuery.data) {
      const d = firstPageQuery.data as { items: unknown[]; hasMore: boolean }
      setItems(d.items as unknown as Item[])
      setHasMore(d.hasMore)
    }
  }, [scheduleStatus]) // eslint-disable-line react-hooks/exhaustive-deps

  const emptyKey =
    scheduleStatus === 'SCHEDULED'
      ? 'gridEmptyScheduled'
      : scheduleStatus === 'PUBLISHED'
        ? 'gridEmptyPublished'
        : 'gridEmptyUnscheduled'

  const groups = groupConsecutiveByPlatform(items)

  return (
    <div className="flex flex-col gap-8 w-full sm:w-3/4 lg:w-1/2 mx-auto">
      {firstPageQuery.isLoading && (
        <div className="grid gap-1 grid-cols-2 sm:grid-cols-3">
          {Array.from({ length: 9 }).map((_, i) => (
            <div
              key={i}
              className="aspect-square bg-input/40 animate-pulse rounded-sm"
            />
          ))}
        </div>
      )}

      {!firstPageQuery.isLoading && items.length === 0 && (
        <div className="flex items-center justify-center rounded-3xl border border-dashed border-border bg-input/20 p-12 text-sm text-muted-foreground">
          {t(emptyKey)}
        </div>
      )}

      {groups.map((group, groupIdx) => (
        <section key={groupIdx} className="flex flex-col gap-3">
          <header className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <PlatformIconFor platform={group.platform} className="size-4" />
            <span>{PLATFORM_LABEL[group.platform] ?? group.platform}</span>
          </header>

          <WideList
            items={group.items}
            scheduleStatus={scheduleStatus}
            locale={locale}
            onSelectAction={setSelectedPostId}
            schedulePillLabel={t('schedulePill')}
          />
        </section>
      ))}

      {(isFetchingMore || firstPageQuery.isLoading) && items.length > 0 && (
        <div className="flex justify-center py-4">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      )}

      <div ref={inViewRef} className="h-1" />

      <SocialPostDialog
        postId={selectedPostId}
        onOpenChangeAction={(open) => {
          if (!open) setSelectedPostId(null)
        }}
      />
    </div>
  )
}

/* ── LinkedIn / Facebook layout ───────────────────────────────────────── */

function WideList({
  items,
  scheduleStatus,
  locale,
  onSelectAction,
  schedulePillLabel,
}: {
  items: Item[]
  scheduleStatus: ScheduleTab
  locale: string
  onSelectAction: (id: string) => void
  schedulePillLabel: string
}) {
  return (
    <div className="flex flex-col gap-3">
      {items.map((item) => {
        const publishAt = item.publishAt ? new Date(item.publishAt) : null
        const displayAssets = item.assets.filter((a) => a.url || a.thumbnailUrl)

        return (
          <div
            key={item.id}
            className="relative group cursor-pointer"
            onClick={() => onSelectAction(item.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onSelectAction(item.id)
              }
            }}
          >
            <div className="flex w-full gap-3 items-start text-left rounded-xl bg-input/40 ring-1 ring-foreground/5 hover:ring-foreground/20 transition-all p-3">
              <div className="relative shrink-0 w-[200px] rounded-lg overflow-hidden bg-input/60 flex">
                {displayAssets.length === 0 ? (
                  <div className="h-[200px]" />
                ) : (
                  <SingleAssetThumb asset={displayAssets[0]} />
                )}
                {displayAssets.length > 1 && (
                  <span className="absolute top-1.5 right-1.5 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
                    1/{displayAssets.length}
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-1 min-w-0 flex-1">
                <p className="text-sm leading-snug line-clamp-3">
                  {item.caption}
                </p>
                {item.hashtags.length > 0 && (
                  <p className="text-xs text-blue-500 dark:text-blue-400 line-clamp-1">
                    {item.hashtags.join(' ')}
                  </p>
                )}
                {scheduleStatus === 'SCHEDULED' && publishAt && (
                  <span className="text-[10px] text-muted-foreground mt-0.5">
                    {formatPublishAt(publishAt, locale)}
                  </span>
                )}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function SingleAssetThumb({ asset }: { asset: Asset }) {
  const thumbUrl = asset.thumbnailUrl ?? asset.url
  const isVideo = asset.type === 'VIDEO'

  return (
    <div className="relative w-full">
      {thumbUrl && (
        <img
          src={thumbUrl}
          alt={asset.title ?? ''}
          className="w-full h-auto block"
        />
      )}
      {isVideo && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
          <div className="flex items-center justify-center size-6 rounded-full bg-black/60">
            <Play className="size-3 text-white fill-white" />
          </div>
        </div>
      )}
    </div>
  )
}

function MultiAssetThumbs({ assets }: { assets: Asset[] }) {
  const [slide, setSlide] = useState(0)
  const total = assets.length

  const visibleCount = total
  const thumbWidth = 200 / visibleCount

  const goNext = () => setSlide((s) => Math.min(s + 1, total - 1))
  const goPrev = () => setSlide((s) => Math.max(s - 1, 0))

  return (
    <>
      <div
        className="absolute inset-0 flex"
        style={{ transform: `translateX(-${slide * thumbWidth}%)` }}
      >
        {assets.map((asset) => {
          const thumbUrl = asset.thumbnailUrl ?? asset.url
          const isVideo = asset.type === 'VIDEO'
          return (
            <div
              key={asset.url ?? asset.title ?? ''}
              className="shrink-0 h-full"
              style={{ width: `${thumbWidth}%` }}
            >
              {thumbUrl && (
                <img
                  src={thumbUrl}
                  alt={asset.title ?? ''}
                  className="h-full w-full object-cover"
                />
              )}
              {isVideo && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                  <div className="flex items-center justify-center size-5 rounded-full bg-black/60">
                    <Play className="size-2.5 text-white fill-white" />
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
      {total > 1 && (
        <>
          <button
            type="button"
            onClick={goPrev}
            disabled={slide === 0}
            className="absolute left-1 top-1/2 -translate-y-1/2 size-5 rounded-full bg-black/50 text-white flex items-center justify-center disabled:opacity-0 hover:bg-black/70 transition-colors"
            aria-label="Previous asset"
          >
            <ChevronLeft className="size-3" />
          </button>
          <button
            type="button"
            onClick={goNext}
            disabled={slide >= total - 1}
            className="absolute right-1 top-1/2 -translate-y-1/2 size-5 rounded-full bg-black/50 text-white flex items-center justify-center disabled:opacity-0 hover:bg-black/70 transition-colors"
            aria-label="Next asset"
          >
            <ChevronRight className="size-3" />
          </button>
          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
            {assets.map((_, i) => (
              <div
                key={i}
                className={`size-1 rounded-full ${i === slide ? 'bg-white' : 'bg-white/40'}`}
              />
            ))}
          </div>
        </>
      )}
    </>
  )
}
